import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';

import Navbar       from './components/layout/Navbar';
import Footer       from './components/layout/Footer';
import AdminLayout  from './components/layout/AdminLayout';
import FloatingButtons from './components/common/FloatingButtons';

import HomePage          from './pages/HomePage';
import ProductsPage      from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage          from './pages/CartPage';
import CheckoutPage      from './pages/CheckoutPage';
import OrderConfirmPage  from './pages/OrderConfirmPage';
import LoginPage         from './pages/LoginPage';
import RegisterPage      from './pages/RegisterPage';
import ProfilePage       from './pages/ProfilePage';
import WishlistPage      from './pages/WishlistPage';
import OrdersPage        from './pages/OrdersPage';
import OrderDetailPage   from './pages/OrderDetailPage';
import NotFoundPage      from './pages/NotFoundPage';

import AdminDashboard  from './pages/admin/AdminDashboard';
import AdminProducts   from './pages/admin/AdminProducts';
import AdminOrders     from './pages/admin/AdminOrders';
import AdminUsers      from './pages/admin/AdminUsers';
import AdminCategories from './pages/admin/AdminCategories';
import AdminCoupons    from './pages/admin/AdminCoupons';
import AdminBanners    from './pages/admin/AdminBanners';
import AdminDrafts     from './pages/admin/AdminDrafts';
import AdminSettings   from './pages/admin/AdminSettings';

const STAFF = ['editor', 'moderator', 'admin'];

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner"/></div>;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!STAFF.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loader"><div className="spinner"/></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/admin" replace />;
  return children;
};

const Pub = ({ children }) => (
  <>
    <Navbar/>
    <main style={{ minHeight:'70vh' }}>{children}</main>
    <Footer/>
    <FloatingButtons/>
  </>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/"             element={<Pub><HomePage/></Pub>} />
      <Route path="/products"     element={<Pub><ProductsPage/></Pub>} />
      <Route path="/products/:id" element={<Pub><ProductDetailPage/></Pub>} />
      <Route path="/cart"         element={<Pub><CartPage/></Pub>} />
      <Route path="/login"        element={<Pub><LoginPage/></Pub>} />
      <Route path="/register"     element={<Pub><RegisterPage/></Pub>} />

      <Route path="/checkout"          element={<ProtectedRoute><Pub><CheckoutPage/></Pub></ProtectedRoute>} />
      <Route path="/order-confirm/:id" element={<ProtectedRoute><Pub><OrderConfirmPage/></Pub></ProtectedRoute>} />
      <Route path="/profile"           element={<ProtectedRoute><Pub><ProfilePage/></Pub></ProtectedRoute>} />
      <Route path="/wishlist"          element={<ProtectedRoute><Pub><WishlistPage/></Pub></ProtectedRoute>} />
      <Route path="/orders"            element={<ProtectedRoute><Pub><OrdersPage/></Pub></ProtectedRoute>} />
      <Route path="/orders/:id"        element={<ProtectedRoute><Pub><OrderDetailPage/></Pub></ProtectedRoute>} />

      <Route path="/admin" element={<AdminRoute><AdminLayout/></AdminRoute>}>
        <Route index             element={<AdminDashboard/>} />
        <Route path="products"   element={<AdminProducts/>} />
        <Route path="categories" element={<AdminCategories/>} />
        <Route path="banners"    element={<AdminBanners/>} />
        <Route path="orders"     element={<AdminOrders/>} />
        <Route path="coupons"    element={<AdminCoupons/>} />
        <Route path="drafts"     element={<AdminDrafts/>} />
        <Route path="users"      element={<SuperAdminRoute><AdminUsers/></SuperAdminRoute>} />
        <Route path="settings"   element={<SuperAdminRoute><AdminSettings/></SuperAdminRoute>} />
      </Route>

      <Route path="*" element={<Pub><NotFoundPage/></Pub>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes/>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: { background:'#1f2937', color:'#fff', borderRadius:'10px', fontSize:'.875rem' },
              success: { iconTheme: { primary:'#16a34a', secondary:'#fff' } },
              error:   { iconTheme: { primary:'#dc2626', secondary:'#fff' } },
            }}
          />
        </Router>
      </CartProvider>
    </AuthProvider>
  );
}
